import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { GenerationJob } from '../../types';
import CustomDropdown from '../gallery/CustomDropdown';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const HistoryPage: React.FC = () => {
  const [jobs, setJobs] = useState<GenerationJob[]>([]);
  const [pagination, setPagination] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    page: 1
  });

  useEffect(() => {
    fetchJobs();
  }, [filters]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      params.append('page', filters.page.toString());
      params.append('limit', '10');

      const response = await axios.get(`${API_BASE}/jobs?${params}`);
      setJobs(response.data.jobs);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'processing': return 'bg-orange-100 text-orange-800';
      case 'queued': return 'bg-blue-100 text-blue-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return '✅';
      case 'processing': return '🔄';
      case 'queued': return '⏳';
      case 'failed': return '❌';
      case 'cancelled': return '🚫';
      default: return '❓';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-start">
        <h2 className="text-3xl font-bold text-gray-900">Generation History</h2>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <CustomDropdown
              options={[
                { value: '', label: 'All Status', icon: '🔍' },
                { value: 'completed', label: 'Completed', icon: '✅' },
                { value: 'processing', label: 'Processing', icon: '🔄' },
                { value: 'queued', label: 'Queued', icon: '⏳' },
                { value: 'failed', label: 'Failed', icon: '❌' },
                { value: 'cancelled', label: 'Cancelled', icon: '🚫' }
              ]}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value, page: 1 })}
              placeholder="All Status"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
            <input
              type="text"
              placeholder="Search job names..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-32 text-gray-600">Loading jobs...</div>
        ) : (
          <div>
            {jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="bg-gray-50 hover:bg-white border border-gray-200 hover:border-blue-300 rounded-xl p-4 transition-all duration-200 cursor-pointer"
                    onClick={() => window.location.href = `/job/${job.id}`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start mb-4">
                      <div>
                        <div className="flex items-center space-x-2 mb-2">
                          <span className="text-lg">{getStatusIcon(job.status)}</span>
                          <h3 className="text-lg font-semibold text-gray-900">{job.job_name}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
                            {job.status}
                          </span>
                        </div>

                        <div className="text-gray-600 text-sm mb-1">{job.workflow_name}</div>
                        <div className="text-xs text-gray-500">
                          Created: {new Date(job.created_at).toLocaleString()}
                        </div>
                      </div>

                      <div className="text-sm text-gray-600">
                        {job.completed_at && (
                          <div className="mb-1">Completed: {new Date(job.completed_at).toLocaleString()}</div>
                        )}
                        {job.batch_size > 0 && (
                          <div className="text-sm font-medium text-blue-600">
                            {job.batch_completed} / {job.batch_size} items
                            {(job.rejection_count ?? 0) > 0 && (
                              <span className="text-red-600 ml-1">({job.rejection_count} rejected)</span>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="text-right">
                        {job.actual_duration && (
                          <div className="text-sm font-medium text-gray-900">
                            {Math.ceil(job.actual_duration / 60)}m {job.actual_duration % 60}s
                          </div>
                        )}
                        <div className="text-xs text-gray-500">Job #{job.id}</div>
                      </div>
                    </div>

                    <div className="flex gap-2 pt-3 border-t border-gray-200">
                      <button
                        onClick={() => window.location.href = `/job/${job.id}`}
                        className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/gallery/${job.id}`;
                        }}
                        className="flex-1 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-lg transition-colors"
                      >
                        View Gallery
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-gray-500 py-12">No jobs found</div>
            )}

            {pagination.pages > 1 && (
              <div className="flex justify-center items-center space-x-4 mt-8">
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  disabled={filters.page <= 1}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                
                <span className="text-sm text-gray-600">
                  Page {pagination.page} of {pagination.pages}
                </span>
                
                <button
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  disabled={filters.page >= pagination.pages}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
